import { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Field } from "./Orders";
import { formatCurrency } from "../lib/format";
import {
    PaintBrush,
    FilmStrip,
    DeviceMobile,
    Globe,
    Robot,
    ShareNetwork,
    Printer,
    GraduationCap,
    TrendUp,
    Star,
    Package,
    Plus,
    TrashSimple,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const ICONS = {
    PaintBrush,
    FilmStrip,
    DeviceMobile,
    Globe,
    Robot,
    ShareNetwork,
    Printer,
    GraduationCap,
    TrendUp,
    Star,
    Package,
};

const ACCENTS = [
    "from-blue-500/30 to-blue-500/0",
    "from-violet-500/30 to-violet-500/0",
    "from-cyan-500/30 to-cyan-500/0",
    "from-emerald-500/30 to-emerald-500/0",
    "from-fuchsia-500/30 to-fuchsia-500/0",
    "from-amber-500/30 to-amber-500/0",
    "from-rose-500/30 to-rose-500/0",
    "from-indigo-500/30 to-indigo-500/0",
    "from-sky-500/30 to-sky-500/0",
    "from-teal-500/30 to-teal-500/0",
];

export default function Services() {
    const { user } = useAuth();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", base_price: 0, icon: "Package" });

    const load = () => setLoading(true) || api.get("/services")
        .then((r) => setServices(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    useEffect(load, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        await api.post("/services", {
            ...form,
            base_price: Number(form.base_price) || 0,
        });
        setShowModal(false);
        setForm({ name: "", description: "", base_price: 0, icon: "Package" });
        load();
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.delete(`/services/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="services-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-uppercase">Catalog</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Service Categories
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Earnings and pipeline broken down by service.
                    </p>
                </div>
                {user?.role === "admin" && (
                    <button
                        data-testid="new-service-btn"
                        onClick={() => setShowModal(true)}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                    >
                        <Plus weight="bold" size={16} /> New Service
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-slate-400 text-sm">Loading…</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {services.map((s, i) => {
                        const Icon = ICONS[s.icon] || Package;
                        return (
                            <div
                                key={s.id}
                                className="glass-card p-5 animate-fade-up relative"
                                data-testid={`service-card-${s.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div
                                        className={`h-11 w-11 rounded-xl bg-gradient-to-br ${
                                            ACCENTS[i % ACCENTS.length]
                                        } grid place-items-center border border-white/10`}
                                    >
                                        <Icon size={22} weight="duotone" />
                                    </div>
                                    {user?.role === "admin" && (
                                        <button
                                            onClick={() => onDelete(s.id)}
                                            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400 opacity-0 hover:opacity-100 transition"
                                            title="Delete"
                                        >
                                            <TrashSimple size={14} />
                                        </button>
                                    )}
                                </div>
                                <div className="mt-3 font-display text-lg font-semibold">
                                    {s.name}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    Base price · {formatCurrency(s.base_price)}
                                </div>
                                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                            Orders
                                        </div>
                                        <div className="font-display font-bold mt-1">
                                            {s.total_orders}
                                        </div>
                                    </div>
                                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                            Pending
                                        </div>
                                        <div className="font-display font-bold mt-1 text-amber-300">
                                            {s.pending}
                                        </div>
                                    </div>
                                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                            Earn
                                        </div>
                                        <div className="font-display font-bold mt-1 text-emerald-300 text-xs">
                                            {formatCurrency(s.earnings)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <Modal title="New Service" onClose={() => setShowModal(false)}>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <Field label="Name" required>
                            <input
                                data-testid="service-name"
                                required
                                className="input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </Field>
                        <Field label="Description">
                            <input
                                className="input"
                                value={form.description}
                                onChange={(e) =>
                                    setForm({ ...form, description: e.target.value })
                                }
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Base Price (₹)">
                                <input
                                    type="number"
                                    className="input"
                                    value={form.base_price}
                                    onChange={(e) =>
                                        setForm({ ...form, base_price: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Icon">
                                <select
                                    className="input"
                                    value={form.icon}
                                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                                >
                                    {Object.keys(ICONS).map((k) => (
                                        <option key={k} value={k}>
                                            {k}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="save-service"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
